/**
 * Dialog Helper Utility
 * 
 * Provides wrappers for legacy dialog replacements
 * Used during migration from window.alert/confirm/prompt to toast notifications
 * 
 * Usage:
 * import { confirmDialog, promptDialog } from '../utils/dialogHelper';
 * const confirmed = await confirmDialog('Are you sure?');
 */

import toast from 'react-hot-toast';
import { colors } from '../styles/designTokens';

/**
 * Show a confirmation dialog
 * Replaces window.confirm()
 */
export const confirmDialog = (message, options = {}) => {
  return new Promise((resolve) => {
    toast(
      (t) => (
        <div>
          <p style={{ marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>{message}</p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(false);
              }}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: '1px solid #D1D5DB',
                background: '#fff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(true);
              }}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: 'none',
                background: options.variant === 'danger' ? colors.error.main : colors.primary.main,
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              {options.confirmText || 'OK'}
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        position: 'top-center',
        style: {
          background: '#fff',
          color: '#374151',
          border: '1px solid #E5E7EB',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          fontSize: '0.875rem',
          fontWeight: 400,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          maxWidth: '500px',
          minWidth: '300px'
        }
      }
    );
  });
};

/**
 * Show a prompt dialog
 * Replaces window.prompt()
 */
export const promptDialog = (message, defaultValue = '', options = {}) => {
  return new Promise((resolve) => {
    let inputValue = defaultValue;
    
    toast(
      (t) => (
        <div>
          <p style={{ marginBottom: '1rem' }}>{message}</p>
          <input
            type="text"
            defaultValue={defaultValue}
            onChange={(e) => { inputValue = e.target.value; }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                toast.dismiss(t.id);
                resolve(inputValue);
              }
            }}
            autoFocus
            style={{
              width: '100%',
              padding: '0.5rem',
              marginBottom: '1rem',
              borderRadius: '0.375rem',
              border: '1px solid #D1D5DB',
              fontSize: '0.875rem'
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(null);
              }}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: '1px solid #D1D5DB',
                background: '#fff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(inputValue);
              }}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: 'none',
                background: colors.primary.main,
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              {options.confirmText || 'OK'}
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        position: 'top-center',
        style: {
          background: '#fff',
          color: '#374151',
          border: '1px solid #E5E7EB',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          fontSize: '0.875rem',
          fontWeight: 400,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          maxWidth: '500px',
          minWidth: '300px'
        }
      }
    );
  });
};

